import type { Message, MessageSource } from '../types/messages';
import { parseMarkdownSync, parseMarkdown } from '../utils/markdown';

/**
 * Single message bubble component.
 */

export interface MessageProps {
  message: Message;
  avatarUrl?: string;
  onCopyCode?: (code: string) => void;
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void;
}

export function createMessage(props: MessageProps): HTMLDivElement {
  const { message, avatarUrl } = props;

  const container = document.createElement('div');
  container.className = `message ${message.role}`;
  container.dataset.messageId = message.id;

  // Avatar (only for assistant messages)
  if (message.role === 'assistant' && avatarUrl) {
    const avatar = document.createElement('img');
    avatar.className = 'message-avatar';
    avatar.src = avatarUrl;
    avatar.alt = 'Assistant';
    avatar.loading = 'lazy';
    container.appendChild(avatar);
  }

  // Message bubble
  const bubble = document.createElement('div');
  bubble.className = 'message-bubble';

  if (message.role === 'user') {
    // User messages - plain text directly in bubble (no stack wrapper)
    bubble.textContent = message.content;
    container.appendChild(bubble);
    return container;
  }

  // Assistant messages - parse markdown in content wrapper
  const content = document.createElement('div');
  content.className = 'message-content';

  if (message.isStreaming) {
    // Use sync parser while streaming (no highlighting)
    content.innerHTML = parseMarkdownSync(message.content);
  } else {
    // Use async parser with highlighting when done
    content.innerHTML = parseMarkdownSync(message.content);
    parseMarkdown(message.content).then((html) => {
      content.innerHTML = html;
      // Set up copy button handlers
      setupCopyButtons(content, props.onCopyCode);
    }).catch((error) => {
      console.error('[LizaDocs] Failed to render message markdown:', error);
      content.innerHTML = parseMarkdownSync(message.content);
    });
  }

  bubble.appendChild(content);

  // Stack groups bubble + sources + feedback so they align left under the avatar.
  const stack = document.createElement('div');
  stack.className = 'message-stack';
  stack.appendChild(bubble);

  if (message.sources?.length) {
    stack.appendChild(createSourcesStrip(message.sources));
  }

  // Feedback sits under the bubble (outside it) for completed assistant messages.
  if (!message.isStreaming && message.id !== 'initial') {
    const feedbackEl = createFeedbackButtons(
      message.id,
      props.onFeedback,
      message.feedback,
      message.created_at
    );
    stack.appendChild(feedbackEl);
  }

  container.appendChild(stack);

  return container;
}

/**
 * Create the sources strip (citation pills) shown under an assistant bubble.
 */
export function createSourcesStrip(sources: MessageSource[]): HTMLDivElement {
  const strip = document.createElement('div');
  strip.className = 'message-sources';

  const label = document.createElement('span');
  label.className = 'message-sources-label';
  label.textContent = 'Sources';
  strip.appendChild(label);

  for (const source of sources) {
    strip.appendChild(createSource(source));
  }

  return strip;
}

/**
 * A single source pill: an anchor when a url is present, otherwise a
 * non-navigating button.
 */
function createSource(source: MessageSource): HTMLElement {
  const element = source.url
    ? document.createElement('a')
    : document.createElement('button');
  element.className = 'message-source';

  if (source.url) {
    const anchor = element as HTMLAnchorElement;
    anchor.href = source.url;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  } else {
    (element as HTMLButtonElement).type = 'button';
  }

  const favicon = document.createElement('span');
  favicon.className = 'message-source-favicon';
  favicon.appendChild(createFileGlyph());
  element.appendChild(favicon);

  const title = document.createElement('span');
  title.className = 'message-source-title';
  title.textContent = source.title;
  element.appendChild(title);

  return element;
}

/**
 * Inline document glyph for a source pill. Built with createElementNS rather
 * than innerHTML to match the codebase's SVG security pattern.
 */
function createFileGlyph(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');

  const body = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  body.setAttribute('d', 'M3 2.5h7l3 3V13.5H3z');
  body.setAttribute('stroke', 'currentColor');
  body.setAttribute('stroke-width', '1.4');
  body.setAttribute('stroke-linejoin', 'round');

  const fold = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  fold.setAttribute('d', 'M9.5 2.5V6h3.5');
  fold.setAttribute('stroke', 'currentColor');
  fold.setAttribute('stroke-width', '1.4');
  fold.setAttribute('stroke-linejoin', 'round');

  svg.appendChild(body);
  svg.appendChild(fold);

  return svg;
}

/**
 * Update an existing message element with new content.
 */
export function updateMessageContent(
  element: HTMLDivElement,
  content: string,
  isStreaming: boolean,
  onCopyCode?: (code: string) => void
): Promise<void> {
  const contentEl = element.querySelector('.message-content');
  if (!contentEl) return Promise.resolve();

  if (isStreaming) {
    contentEl.innerHTML = parseMarkdownSync(content);
    return Promise.resolve();
  } else {
    return parseMarkdown(content).then((html) => {
      contentEl.innerHTML = html;
      setupCopyButtons(contentEl as HTMLElement, onCopyCode);
    }).catch((error) => {
      console.error('[LizaDocs] Failed to update message markdown:', error);
      contentEl.innerHTML = parseMarkdownSync(content);
    });
  }
}

/**
 * Set up copy button click handlers.
 */
function setupCopyButtons(container: HTMLElement, onCopyCode?: (code: string) => void): void {
  const copyButtons = container.querySelectorAll('.copy-button');

  copyButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const code = decodeURIComponent(button.getAttribute('data-code') || '');
      if (!code) return;

      try {
        await navigator.clipboard.writeText(code);
        button.textContent = 'Copied!';
        button.classList.add('copied');

        setTimeout(() => {
          button.textContent = 'Copy';
          button.classList.remove('copied');
        }, 2000);

        onCopyCode?.(code);
      } catch (err) {
        console.error('Failed to copy:', err);
        button.textContent = 'Failed';
        setTimeout(() => {
          button.textContent = 'Copy';
        }, 2000);
      }
    });
  });
}

/**
 * Create feedback buttons (thumbs up/down), plus an optional relative
 * timestamp derived from the message's created_at.
 */
export function createFeedbackButtons(
  messageId: string,
  onFeedback?: (messageId: string, rating: 'up' | 'down') => void,
  currentFeedback?: 'up' | 'down' | null,
  createdAt?: string
): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'message-feedback';

  const thumbsUp = createThumbButton('up', currentFeedback === 'up');
  const thumbsDown = createThumbButton('down', currentFeedback === 'down');

  thumbsUp.addEventListener('click', () => onFeedback?.(messageId, 'up'));
  thumbsDown.addEventListener('click', () => onFeedback?.(messageId, 'down'));

  container.appendChild(thumbsUp);
  container.appendChild(thumbsDown);

  const relativeTime = formatRelativeTime(createdAt);
  if (relativeTime) {
    const time = document.createElement('span');
    time.className = 'message-feedback-time';
    time.textContent = relativeTime;
    container.appendChild(time);
  }

  return container;
}

/**
 * Short relative time for the feedback row. Returns an empty string when no
 * timestamp is available so the caller can skip rendering the span.
 */
function formatRelativeTime(createdAt?: string): string {
  if (!createdAt) return '';

  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return '';

  const minutes = Math.floor((Date.now() - created) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Create a single thumb button with SVG icon.
 * Note: SVGs are hardcoded constants, not user input - safe to use innerHTML.
 */
function createThumbButton(type: 'up' | 'down', isActive: boolean): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = `feedback-button ${type}${isActive ? ' active' : ''}`;
  button.type = 'button';
  button.setAttribute('aria-label', type === 'up' ? 'Thumbs up' : 'Thumbs down');

  // Create SVG element programmatically for security
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');

  const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');

  if (type === 'up') {
    path1.setAttribute('d', 'M7 10v12');
    path2.setAttribute('d', 'M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z');
  } else {
    path1.setAttribute('d', 'M17 14V2');
    path2.setAttribute('d', 'M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z');
  }

  svg.appendChild(path1);
  svg.appendChild(path2);
  button.appendChild(svg);

  return button;
}

/**
 * Update feedback button state.
 */
export function updateFeedbackState(
  element: HTMLDivElement,
  feedback: 'up' | 'down' | null
): void {
  const feedbackContainer = element.querySelector('.message-feedback');
  if (!feedbackContainer) return;

  const upButton = feedbackContainer.querySelector('.feedback-button.up');
  const downButton = feedbackContainer.querySelector('.feedback-button.down');

  upButton?.classList.toggle('active', feedback === 'up');
  downButton?.classList.toggle('active', feedback === 'down');
}

/**
 * Create loading indicator (typing dots).
 */
export function createLoadingIndicator(): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'message assistant';

  const bubble = document.createElement('div');
  bubble.className = 'message-bubble';

  const loading = document.createElement('div');
  loading.className = 'loading-indicator';

  for (let i = 0; i < 3; i++) {
    const dot = document.createElement('span');
    dot.className = 'loading-dot';
    loading.appendChild(dot);
  }

  bubble.appendChild(loading);
  container.appendChild(bubble);

  return container;
}
